import { Link } from "@inertiajs/react"
import type { PropsWithChildren } from "react"

import oksLogoFade from "@/assets/oks-logo-fade.png"
import { rootPath } from "@/routes"

/**
 * AuthLoginLayout — OKS-475 chrome for the role-gated returning-login
 * surface (`/sign_in`).
 *
 * Distinct from `auth-simple-layout` (shared by password-reset /
 * email-verification) so the login-only muted backdrop never bleeds
 * into those pages. Per UX Spec §2 / §5:
 *
 *   - **Desktop / tablet (≥ md):** a centered ~440px white card on a
 *     full-bleed `--accent-primary-muted` (`#fff4ed`) backdrop.
 *   - **Mobile (402px):** the card goes full-width with 24px side
 *     gutters and NO backdrop.
 *
 * The page is pre-brand-context (brand isolation P0): NO brand name,
 * logo, or cover renders here. The mark is the design-of-record OKS
 * "fade" wordmark (raster) — the panels render their own heading/sub
 * beneath it, so this layout only owns the backdrop, card, and logo.
 */
export default function AuthLoginLayout({ children }: PropsWithChildren) {
  return (
    <div className="flex min-h-svh flex-col items-center justify-center px-6 py-10 md:bg-[var(--accent-primary-muted)] md:p-10">
      <div className="border-border bg-card w-full max-w-[440px] rounded-xl md:border md:p-8 md:shadow-sm">
        <div className="flex flex-col gap-6">
          <div className="flex justify-center">
            <Link href={rootPath()} aria-label="OKS">
              {/*
                OKS-486 — the design-of-record fade-gradient "OKS" wordmark
                (Figma 246:327, exported raster) now ships. Height-only
                sizing (h-10 mobile, h-12 ≥ md) + w-auto preserves the
                ~3.78:1 aspect and the shipped footprint. The mark is
                decorative (`alt=""`); the wrapping Link's `aria-label`
                provides the "OKS" accessible name (no double announcement).
              */}
              <img src={oksLogoFade} alt="" className="h-10 w-auto md:h-12" />
            </Link>
          </div>
          {children}
        </div>
      </div>
    </div>
  )
}
